/**
 * GHL SSO Helper
 * Based on: https://github.com/GoHighLevel/ghl-marketplace-app-template/blob/main/src/ui/src/ghl/index.js
 *
 * Handles SSO authentication for GHL Custom Pages (iframes)
 */
import { debug } from './utils/debug';
export class GHL {
  constructor() {}

  /**
   * Request user data from GHL parent window via SSO
   * Returns decrypted user data including locationId, companyId, etc.
   */
  async getUserData() {
    debug.log('[GHL] Requesting SSO user data from GHL parent window...');
    debug.log('[GHL] window.parent:', window.parent);
    debug.log('[GHL] window.self === window.parent:', window.self === window.parent);

    try {
      // Request encrypted user data from GHL parent window
      const encryptedKey = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          debug.error('[GHL] ⏱️ SSO request timed out after 5 seconds - parent window did not respond');
          reject(new Error('SSO request timed out after 5 seconds'));
        }, 5000);

        debug.log('[GHL] Sending REQUEST_USER_DATA to parent window...');
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");

        const messageHandler = ({ data, origin }) => {
          debug.log('[GHL] Received message from origin:', origin, 'data:', data);
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            debug.log('[GHL] ✅ Received REQUEST_USER_DATA_RESPONSE');
            clearTimeout(timeout);
            window.removeEventListener("message", messageHandler);
            resolve(data.payload);
          }
        };

        debug.log('[GHL] Listening for REQUEST_USER_DATA_RESPONSE...');
        window.addEventListener("message", messageHandler);
      });

      debug.log('[GHL] Received encrypted SSO data, sending to backend for decryption...');

      // Send to backend for decryption
      const res = await fetch('/api/decrypt-sso', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ key: encryptedKey })
      });

      if (!res.ok) {
        throw new Error(`SSO decryption failed: ${res.status}`);
      }

      const data = await res.json();
      debug.log('[GHL] ✅ SSO user data decrypted successfully:', {
        locationId: data.locationId,
        companyId: data.companyId,
        email: data.email
      });

      return data;
    } catch (error) {
      debug.error('[GHL] ❌ SSO error:', error);
      throw error;
    }
  }
}

// Make GHL available globally for easy access
if (typeof window !== 'undefined') {
  window.ghl = new GHL();
}

export default GHL;
